import { ChevronDown, Users } from 'lucide-react';
import type { EmployeeOption } from '../api';
import { parseAssigneeNames } from './FieldEditors';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Every column the assignee filter can match against. Both spellings of the
 * pickup column exist across months (see FORCED_TYPE_BY_NAME), so check both.
 */
const ASSIGNEE_FILTER_COLUMNS = [
  'Bookkeeper',
  'Account Manager',
  'Sales Taxes Assigned To',
  'Payroll Taxes Assigned To',
  'Financial Statements Ready For Pickup',
  'Financial Statements Ready for Pick Up',
];

/**
 * The assignee columns the filter matches for a given tab: only those the
 * tab actually shows, so e.g. the Sales Tax tab filters by "Sales Taxes
 * Assigned To" alone. A tab with no assignee columns (Other) falls back to
 * all of them rather than making the filter dead.
 */
export function assigneeFilterColumns(tabColumns: string[]): string[] {
  const scoped = ASSIGNEE_FILTER_COLUMNS.filter(c => tabColumns.includes(c));
  return scoped.length > 0 ? scoped : ASSIGNEE_FILTER_COLUMNS;
}

// Special assignee options (specialOptions.ts), uppercased like stored
// values — they're not employees, so the filter ignores them
const NON_EMPLOYEE_VALUES = new Set([
  'NA',
  'NO SALES TAX',
  'NO PAYROLL',
  'NO BOOKKEEPING',
]);

/**
 * Every employee name (UPPERCASE, V1's stored casing) assigned in the given
 * assignee columns of the row (from assigneeFilterColumns, so the filter
 * follows the active tab). Shared by the dropdown counts and the row filter
 * so they can never disagree.
 */
export function rowAssigneeNames(
  data: Record<string, unknown>,
  columns: readonly string[]
): Set<string> {
  const names = new Set<string>();
  for (const column of columns) {
    for (const name of parseAssigneeNames(data[column])) {
      if (!NON_EMPLOYEE_VALUES.has(name)) names.add(name);
    }
  }
  return names;
}

interface AssigneeFilterProps {
  employees: EmployeeOption[];
  /** Row count per UPPERCASE name within the active tab's population */
  counts: Map<string, number>;
  /** Selected UPPERCASE names; empty set = no filtering */
  selected: Set<string>;
  onToggle: (name: string) => void;
  onClear: () => void;
}

/**
 * Multi-select employee filter: shows rows where any selected employee
 * appears in one of the ACTIVE TAB's assignee columns (e.g. Bookkeeper /
 * Account Manager on the Bookkeeping tab, Sales Taxes Assigned To on the
 * Sales Tax tab — see assigneeFilterColumns). Selecting keeps the menu open
 * (like the Bookkeeper editor) so several employees can be picked in one
 * sitting.
 */
export default function AssigneeFilter({
  employees,
  counts,
  selected,
  onToggle,
  onClear,
}: AssigneeFilterProps) {
  // Only names with rows in the current population are listed (like the
  // status chips); a selected name stays listed at 0 so it can be unchecked
  const visible = (upper: string) =>
    (counts.get(upper) ?? 0) > 0 || selected.has(upper);

  const sorted = employees
    .filter(e => visible(e.name.toUpperCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Legacy names still assigned in the data (or still selected) but no
  // longer in the employee list — keep them filterable
  const extraNames = [...new Set([...counts.keys(), ...selected])]
    .filter(
      name =>
        visible(name) && !employees.some(e => e.name.toUpperCase() === name)
    )
    .sort((a, b) => a.localeCompare(b));

  const properName = (upper: string) =>
    employees.find(e => e.name.toUpperCase() === upper)?.name ?? upper;

  const label =
    selected.size === 0
      ? 'Assignee'
      : selected.size === 1
        ? properName([...selected][0])
        : `${selected.size} assignees`;

  const optionRow = (key: string, name: string, upper: string) => (
    <DropdownMenuCheckboxItem
      key={key}
      checked={selected.has(upper)}
      onSelect={e => e.preventDefault()}
      onCheckedChange={() => onToggle(upper)}
      className={cn('text-foreground', selected.has(upper) && 'font-medium')}
    >
      <span className="truncate">{name}</span>
      <span className="ml-auto pl-2 text-xs text-muted-foreground">
        {counts.get(upper) ?? 0}
      </span>
    </DropdownMenuCheckboxItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Filter by assigned employee"
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
            selected.size > 0
              ? 'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
              : 'border-border text-muted-foreground hover:text-foreground'
          )}
        >
          <Users className="h-3.5 w-3.5" />
          {label}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        onCloseAutoFocus={e => e.preventDefault()}
        className="max-h-72 w-56 overflow-y-auto"
      >
        {sorted.map(e => optionRow(e.id, e.name, e.name.toUpperCase()))}
        {extraNames.map(name => optionRow(name, name, name))}
        {selected.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onClear}
              className="text-muted-foreground"
            >
              Clear filter
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
